import { lazy, Suspense, useEffect } from "react";
import { useAuth } from "./AuthContext";
import AuthScreen from "./AuthScreen";
import HomeDataStatus from "./app/HomeDataStatus";
import useOnboardingStatus from "./app/useOnboardingStatus";
import { safeSessionSet } from "./browserStorage";
import { FEATURE_FLAGS } from './featureFlags';

const OnboardingScreen = lazy(() => import("./OnboardingScreen"));
const AppAuthenticated = lazy(() => import("./app/AppAuthenticated"));
const ClassesStaffApp = lazy(() => import('./classSeasons/ClassesStaffApp'));

function StartupShell({ message = "Preparing your Home…" }) {
  return (
    <main
      aria-busy="true"
      aria-label={message}
      style={{
        minHeight: "100vh",
        background: "var(--app-bg, #080808)",
        color: "var(--text, #f7f4ef)",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "min(100%, 430px)", textAlign: "center" }}>
        <p
          style={{
            color: "var(--brand-coral, #ff6b35)",
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.16em",
            margin: "0 0 12px",
            textTransform: "uppercase",
          }}
        >
          Rise with the tribe
        </p>
        <h1
          style={{
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            margin: 0,
          }}
        >
          TribeLog
        </h1>
        <p
          style={{
            color: "var(--text-soft, #a3a3a3)",
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
            fontSize: 13,
            margin: "12px 0 0",
          }}
        >
          {message}
        </p>
      </div>
    </main>
  );
}

function capturePendingChallengeInvite() {
  const params = new URLSearchParams(window.location.search);
  const joinCode = params.get("join") || params.get("code");
  const referralUid = params.get("ref");
  const sanitizedCode = joinCode?.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
  const sanitizedReferral = referralUid?.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 128);

  if (sanitizedCode) {
    safeSessionSet("pendingJoinCode", sanitizedCode);
  }
  if (sanitizedReferral) {
    safeSessionSet("pendingReferralUid", sanitizedReferral);
  }
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function TribeChallenge() {
  const { user } = useAuth();
  const isClassesWorkspace = typeof window !== 'undefined' && window.location.pathname.startsWith('/classes');
  const {
    completeOnboarding,
    onboarded,
    onboardingStatus,
    retryOnboardingStatus,
  } = useOnboardingStatus(user);

  useEffect(() => {
    capturePendingChallengeInvite();
  }, []);

  if (user === undefined) {
    return <StartupShell message="Getting your account ready…" />;
  }

  if (isClassesWorkspace) {
    if (!FEATURE_FLAGS.CLASS_SEASONS_ENABLED) {
      return (
        <main style={{ minHeight: '100vh', background: 'var(--app-bg, #080808)', color: 'var(--text, #fff)', display: 'grid', placeItems: 'center', padding: 24 }}>
          <div><h1>Classes unavailable</h1><p>This private pilot is not enabled for this build.</p></div>
        </main>
      );
    }
    if (!user) return <AuthScreen />;
    return <Suspense fallback={<StartupShell message="Loading trainer workspace…" />}><ClassesStaffApp /></Suspense>;
  }

  if (!user) return <AuthScreen />;

  if (onboarded === null) {
    if (["offline", "timeout", "error"].includes(onboardingStatus)) {
      return (
        <div style={{ minHeight: "100vh", background: "var(--app-bg)" }}>
          <HomeDataStatus
            hasSnapshot={false}
            retry={retryOnboardingStatus}
            status={onboardingStatus}
          />
        </div>
      );
    }
    return (
      <div style={{ minHeight: "100vh", background: "var(--app-bg)", display: "grid", placeItems: "center", color: "var(--text)" }}>
        <div style={{ textAlign: "center" }}>
          <span aria-hidden="true" style={{ fontSize: 32 }}>🏃</span>
          <p style={{ color: "var(--text-soft)", fontSize: 13, margin: "12px 0 0" }}>
            Preparing your Home…
          </p>
        </div>
      </div>
    );
  }

  if (!onboarded) {
    return (
      <Suspense fallback={<StartupShell message="Preparing your first log…" />}>
        <OnboardingScreen
          user={user}
          userName={user.displayName}
          onComplete={completeOnboarding}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<StartupShell message="Loading your Home…" />}>
      <AppAuthenticated key={user.uid} user={user} />
    </Suspense>
  );
}
